import React, { useState, useEffect, useCallback, Suspense } from "react";
import ReactHtmlParser from 'html-react-parser'; 
import { Link } from 'react-router-dom';
import axios from "axios";
import { Helmet } from 'react-helmet';
// Lazy load the components
const TopDefiProtocols = React.lazy(() => import('./TopDefiProtocols'));
const TopL2Protocols = React.lazy(() => import('./TopL2Protocols'));
const OpinionArticles = React.lazy(() => import('./OpinionArticles'));
const PressReleases = React.lazy(() => import('./PressReleases'));
const LatestBotNews = React.lazy(() => import('./LatestBotNews'));

const TopNFTCollections = React.lazy(() => import('./TopNFTCollections'));
const RandomImageComponent = React.lazy(() => import('./RandomImageComponent'));
import ErrorBoundary from './ErrorBoundary'; 
import { getOptimizedThumbnail, checkAvifSupport } from "./frontendUtils.js"; 
import Err from '../assets/err.webp'; 

const FrontPage = ({ setFrontPageIds }) => {
  const [frontPageContent, setFrontPageContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [optimizedWidth, setOptimizedWidth] = useState(window.innerWidth <= 480 ? 400 : 900);
  const [supportsAvif, setSupportsAvif] = useState(null);

  const fetchFrontPageContent = useCallback(async () => {
    const maxRetries = 3; // Maximum number of retries
    let retryCount = 0;

    const fetchData = async () => {
      try {

        // Check sessionStorage for cached data
        const cachedContent = sessionStorage.getItem('frontPageContent');
        if (cachedContent) {
          const data = JSON.parse(cachedContent);
          setFrontPageContent(data);

          // Collect article IDs and set them for exclusion
          const ids = Object.values(data)
            .filter((article) => article) // Ensure the category has content
            .map((article) => article.id);
          setFrontPageIds(ids);

          setLoading(false);
          setError(false); // Reset error state on success
          return; // Exit early if session cache is used
        }

        // If no sessionStorage, fetch from the API
        const response = await axios.get(`/api/frontpage`);

        const fetchedData = response.data; 


        //Ensure correct naming for `big image`
        const sectionsToOptimize = ["featured", "third", "big image"];

        //Create a processed copy of fetchedData
        const processedContent = { ...fetchedData };

        await Promise.all(
          sectionsToOptimize.map(async (key) => {
            const mappedKey = key;
            
            if (fetchedData[mappedKey]?.thumbnail) {

              processedContent[mappedKey].optimizedThumbnail = await getOptimizedThumbnail(
                fetchedData[mappedKey].thumbnail,
              );
            } else {
              //console.warn(`No thumbnail found for ${mappedKey}`);
            }
          })
        );

        sessionStorage.setItem("frontPageContent", JSON.stringify(processedContent));

        setFrontPageContent(processedContent); 

        // Collect article IDs and set them for exclusion
        const ids = Object.values(response.data)
          .filter((article) => article) // Ensure the category has content
          .map((article) => article.id);
        setFrontPageIds(ids);

        setLoading(false);
        setError(false); // Reset error state on success
      } catch (error) {
        //console.error(`Error fetching frontpage content (attempt ${retryCount + 1}):`, error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(fetchData, 500); // Retry after 500ms
        } else {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchData(); // Initial fetch attempt
  }, [setFrontPageIds]);

  useEffect(() => {
    fetchFrontPageContent();
  }, [fetchFrontPageContent]);

  if (loading) {
    return (
      <div style={{ height: "1000px" }} className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-18">
        
      </div>
    );
  }

if (error) {
  return <div>
    <img 
      src={Err}  
      alt="500 error page illustration" 
      style={{ width: '100%', maxWidth: '100%' }}
    />
  </div>;
}


  const { featured, second, third, fourth, fifth, 'big image': bigImage } = frontPageContent;

  return (
   
    <div className="z-2 mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-6 mt-18" style={{ backgroundColor: 'var(--background-color)' }}>
     
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
        
        {/* Featured */}
        <div className="md:order-2 lg:order-2 sm:order-1 lg:col-span-2 md:col-span-1">
          {featured && (
            <>
              <Helmet>
                <link rel="prefetch" href={featured.pageUrl} />
              </Helmet>
              <Link to={featured.pageUrl} className="block">

              <picture>
                {featured.optimizedThumbnail?.avifMobile && (
                  <source
                    srcSet={featured.optimizedThumbnail.avifMobile}
                    type="image/avif"
                    media="(max-width: 480px)"
                  />
                )}
                {featured.optimizedThumbnail?.avif && (
                  <source
                    srcSet={featured.optimizedThumbnail.avif}
                    type="image/avif"
                  />
                )}
                {featured.optimizedThumbnail?.webp && (
                  <source
                    srcSet={featured.optimizedThumbnail.webp}
                    type="image/webp"
                  />
                )}
                <img
                  className="w-full h-auto"
                  src={featured.optimizedThumbnail?.fallback || featured.optimizedThumbnail?.webp}
                  alt={featured.title}
                  width={optimizedWidth}
                  height={
                    featured.thumbnailWidth && featured.thumbnailHeight
                      ? (optimizedWidth * featured.thumbnailHeight) / featured.thumbnailWidth
                      : 628
                  }
                  style={{
                    aspectRatio: `${featured.thumbnailWidth || 1200} / ${featured.thumbnailHeight || 628}`,
                  }}
                />
              </picture>

                {featured.imageCaption && (
                  <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-2 text-right">
                    {featured.imageCaption}
                  </p>
                )}
                <h2 className="mb-4 font-semibold" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.6875rem', lineHeight: '1.35em', fontWeight: '500' }}>
                  {featured.title}
                </h2>
                <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1rem', lineHeight: '1.56em' }}>
                  {ReactHtmlParser(featured.summary)}
                </p>
              </Link>
            </>
          )}
        </div>

        {/* Second & Fifth */}
        <div className="order-3 md:order-1 lg:order-1 grid grid-cols-1 gap-4 sm:border-r-0 sm:pr-4 border-t pt-4 md:border-t-0 md:pt-0 md:border-r md:pr-4 md:col-span-1 lg:col-span-1">
          {second && (
            <>
              <Helmet>
                <link rel="prefetch" href={second.pageUrl} />
              </Helmet>
              <Link to={second.pageUrl}>
                <h2 className="mb-4 font-medium" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.6875rem', lineHeight: '1.35em', fontWeight: '500' }}>
                  {second.title}
                </h2>
                <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '1.125rem', lineHeight: '1.55em' }}>
                  {ReactHtmlParser(second.summary)}
                </p>
              </Link>
            </>
          )}
          {fifth && (
            <>
              <Helmet>
                <link rel="prefetch" href={fifth.pageUrl} />
              </Helmet>
              <Link to={fifth.pageUrl}>
                <h2 className="mb-4 border-t pt-5 font-semibold" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                  {fifth.title}
                </h2>
                <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '0.9375rem', lineHeight: '1.55em' }}>
                  {ReactHtmlParser(fifth.summary)} 
                </p>
              </Link>
            </>
          )}
        </div>

        {/* Third & Fourth */}
        <div className="order-2 lg:order-3 lg:col-span-1 md:col-span-2 grid grid-cols-1 lg:border-l lg:pl-4 gap-4 pt-4 border-t lg:border-t-0 lg:pt-0">
          {third && (
            <>
              <Helmet>
                <link rel="prefetch" href={third.pageUrl} />
              </Helmet>
              <Link to={third.pageUrl}>
                <picture>
                {third.optimizedThumbnail?.avifMobile && (
                  <source
                    srcSet={third.optimizedThumbnail.avifMobile}
                    type="image/avif"
                    media="(max-width: 480px)"
                  />
                )}
                {third.optimizedThumbnail?.avif && (
                  <source
                    srcSet={third.optimizedThumbnail.avif}
                    type="image/avif"
                  />
                )}
                {third.optimizedThumbnail?.webp && (
                  <source
                    srcSet={third.optimizedThumbnail.webp}
                    type="image/webp"
                  />
                )}
                <img
                  className="w-full h-auto"
                  src={third.optimizedThumbnail?.fallback || third.optimizedThumbnail?.webp}
                  alt={third.title}
                  width={optimizedWidth || 1200}
                  height={
                    third.thumbnailWidth && third.thumbnailHeight
                      ? (optimizedWidth * third.thumbnailHeight) / third.thumbnailWidth
                      : 628
                  }
                  style={{
                    aspectRatio: `${third.thumbnailWidth || 1200} / ${third.thumbnailHeight || 628}`
                  }}
                />
              </picture>

                {third.imageCaption && (
                  <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-2 text-right">
                    {third.imageCaption}
                  </p>
                )}
                <h2 className="mt-4 pb-10 border-b" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                  {third.title}
                </h2>
              </Link>
            </>
          )}
          {fourth && (
            <>
              <Helmet>
                <link rel="prefetch" href={fourth.pageUrl} />
              </Helmet>
              <Link to={fourth.pageUrl}>
                
                  <h2 className="mb-4 font-semibold" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: '1.35em', fontWeight: '500' }}>
                    {fourth.title}
                  </h2>
                  <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '0.9375rem', lineHeight: '1.55em' }}>
                    {ReactHtmlParser(fourth.summary)}
                  </p>
                
              </Link>
            </>
          )}
        </div>

      </div>
          
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4 xl:gap-6 mt-4">
          <ErrorBoundary>
             <Suspense>
            
                <div className="hidden-sm col-span-1 space-y-6 pr-2 border-t border-r justify-between" style={{borderColor: 'var(--border-color)'}}>

                  <TopDefiProtocols />

                </div>
                <div className=" md:col-span-1 overflow-hidden justify-between" style={{backgroundColor: 'var(--background-color)'}}>
                 
                  <TopL2Protocols />

                </div>

                <div className="hidden-sm col-span-1 overflow-hidden justify-between" style={{backgroundColor: 'var(--background-color)'}}>
                 
                  <TopNFTCollections />

                </div>

                <div className=" md:col-span-1 space-y-6 border-t lg:border-l lg:pl-4" style={{borderColor: 'var(--border-color)'}}>

                  <OpinionArticles />

                </div>
              </Suspense>
            </ErrorBoundary>

        {/* Render Big Image category */}
          {bigImage && (
            <>
              <Helmet>
                <link rel="prefetch" href={`${bigImage.optimizedThumbnail}`} />
              </Helmet>
              <div className="md:col-start-1 md:col-span-4 overflow-hidden border-t pt-4" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--background-color)' }}>
                <div className="md:grid md:grid-cols-4 gap-4 overflow-hidden" style={{ backgroundColor: 'var(--background-color)' }}>
                  {/* Left: Big Image Block */}
                  <div className="md:col-span-3">
                    <Link to={bigImage.pageUrl}>
                      <picture>
                      {bigImage.optimizedThumbnail?.avifMobile && (
                        <source
                          srcSet={bigImage.optimizedThumbnail.avifMobile}
                          type="image/avif"
                          media="(max-width: 480px)"
                        />
                      )}
                      {bigImage.optimizedThumbnail?.avif && (
                        <source
                          srcSet={bigImage.optimizedThumbnail.avif}
                          type="image/avif"
                        />
                      )}
                      {bigImage.optimizedThumbnail?.webp && (
                        <source
                          srcSet={bigImage.optimizedThumbnail.webp}
                          type="image/webp"
                        />
                      )}
                      <img
                        src={bigImage.optimizedThumbnail?.fallback || bigImage.optimizedThumbnail?.webp}
                        alt={bigImage.title}
                        width={optimizedWidth || 1200}
                        height={
                          bigImage.thumbnailWidth && bigImage.thumbnailHeight
                            ? (optimizedWidth * bigImage.thumbnailHeight) / bigImage.thumbnailWidth
                            : 628
                        }
                        style={{
                          aspectRatio: `${bigImage.thumbnailWidth || 1200} / ${bigImage.thumbnailHeight || 628}`
                        }}
                        className="w-full h-auto"
                      />
                    </picture>

                    </Link>
                    {bigImage.imageCaption && (
                      <p className="text-neutral-400 dark:text-gray-500 text-[0.69rem] mt-1 mb-7 mr-0 text-right">{bigImage.imageCaption}</p>
                    )}
                  </div>
                  {/* Right: Text Content and Random Image Block */}
                  <div className="md:col-span-1 mt-4 md:mt-0">
                    <Link to={bigImage.pageUrl}>
                      <h2 className="mb-4 font-semibold" style={{ fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif', color: 'var(--text-color)', fontSize: '27px', lineHeight: '1.35em', fontWeight: '500' }}>
                        {bigImage.title}
                      </h2>
                      <p className="mb-4" style={{ fontFamily: 'var(--font-paragraph)', color: 'var(--text-color)', fontWeight: '400', fontSize: '16px', lineHeight: '1.56em' }}>
                        {ReactHtmlParser(bigImage.summary)}
                      </p>
                    </Link>
                    {/* Render the Random Image Block */}
                    <div className="mb-4 mt-10 border-t pt-7">
                      <ErrorBoundary>
                        <Suspense>
                          <RandomImageComponent showAds supportsAvif={supportsAvif} />
                        </Suspense>
                      </ErrorBoundary>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="md:col-span-4 border-t overflow-hidden justify-between" style={{borderColor: 'var(--border-color)'}}> 
              <ErrorBoundary>
                <Suspense>
                  <LatestBotNews />  
                </Suspense>
              </ErrorBoundary>

            </div>

          <div className="md:col-span-4 border-t overflow-hidden justify-between" style={{borderColor: 'var(--border-color)'}}> 
              <ErrorBoundary>
                <Suspense>
                  <PressReleases supportsAvif={supportsAvif} />  
                </Suspense>
              </ErrorBoundary>

            </div>

          </div>

        </div>
  );
};

export default FrontPage;

